import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd } from '@angular/router';
import { logger } from '@fmnts/common/log';
import * as O from 'effect/Option';
import { MatomoRouterInterceptor, MatomoTracker } from 'ngx-matomo-client';
import { EMPTY, Observable } from 'rxjs';
import {
  SHARED_VENDORS_MATOMO_SAMPLE_RATE_CONFIG,
  SHARED_VENDORS_MATOMO_TITLE_STREAM,
  SHARED_VENDORS_MATOMO_USER_STREAM,
} from './matomo-interop.tokens';
import { MatomoSampleRateConfig, MatomoUser } from './matomo.model';

/**
 * The default sample rate for all trackings.
 *
 * Uses a float between [0,1]
 */
const DEFAULT_SAMPLE_RATE = 1;

/**
 * Service that provides interop with Matomo tracker.
 */
@Injectable()
export class MatomoInteropService {
  private readonly tracker = inject(MatomoTracker);

  constructor() {
    this._setupTitleState();
    this._setupUserState();
  }

  /**
   * Sets up syncing the page title with Matomo tracker.
   */
  private _setupTitleState(
    titleFeature = O.fromNullable(
      inject(SHARED_VENDORS_MATOMO_TITLE_STREAM, { optional: true }),
    ),
  ): void {
    this._fromOptionalFeatureFactory(titleFeature)
      .pipe(takeUntilDestroyed())
      .subscribe((title) => {
        this.tracker.setDocumentTitle(title);
      });
  }

  private _setupUserState(
    userFeature = O.fromNullable(
      inject(SHARED_VENDORS_MATOMO_USER_STREAM, { optional: true }),
    ),
  ): void {
    this._fromOptionalFeatureFactory(userFeature)
      .pipe(takeUntilDestroyed())
      .subscribe((user) => {
        this.updateUser(user);
      });
  }

  private updateUser(user: O.Option<MatomoUser>): void {
    if (O.isSome(user)) {
      this.tracker.setUserId(user.value.id);
    } else {
      this.tracker.resetUserId();
    }
  }

  private _fromOptionalFeatureFactory<T>(
    feature: O.Option<Observable<T>>,
  ): Observable<T> {
    return feature.pipe(O.getOrElse(() => EMPTY));
  }
}

/**
 * Interceptor to drop routing-events from sending to Matomo
 *
 * 1. Register the interceptor for Matomo, using
 * ```
 *  withRouterInterceptors([MatomoSampleRateInterceptor]),
 * ```
 *
 * 2. Configure sample rates using `withCustomSampleRates`
 */
@Injectable()
export class MatomoSampleRateInterceptor implements MatomoRouterInterceptor {
  private readonly tracker = inject(MatomoTracker);
  private readonly config = inject(SHARED_VENDORS_MATOMO_SAMPLE_RATE_CONFIG, {
    optional: true,
  });
  private readonly log = logger({ name: 'SampleRateInterceptor' });

  beforePageTrack(event: NavigationEnd): void {
    const sampleRand = Math.random();
    const customSampleRate =
      getSampleRate(event.url, this.config) ?? DEFAULT_SAMPLE_RATE;

    const shouldSkipEvent = sampleRand > customSampleRate;

    if (shouldSkipEvent) {
      this.tracker.optUserOut();
      this.log.trace(
        `Dropping tracking event for route ${event.url} with custom sample rate of ${customSampleRate}`,
      );
    } else {
      this.tracker.forgetUserOptOut();
    }
  }
}

/**
 * @param url current active URL
 * @param config
 * @returns a custom sample rate for specific routes or `undefined`
 */
function getSampleRate(
  url: string,
  config: MatomoSampleRateConfig | null,
): number | undefined {
  if (!config) {
    return undefined;
  }

  // test the regex-patterns with the current route
  const trackableUrls = config.routes.filter((route) => route.page.test(url));

  if (trackableUrls.length === 0) {
    return undefined;
  }

  if (trackableUrls.length === 1) {
    return trackableUrls[0].sampleRate;
  }

  // if more than one rule apply to the current URL,
  // we shoult return the most specific route
  trackableUrls.sort((a, b) => b.page.source.length - a.page.source.length);
  return trackableUrls[0].sampleRate;
}
